import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useCallback } from 'react';

import { JONUM_VERIFICATION_REQUIRED_CODE } from '@sorare/core/src/components/jonum/JonumVerificationDialog/useJonumVerificationGuard';
import { DialogKey } from '@sorare/core/src/components/navigation/WithDialogs';
import { Level } from '@sorare/core/src/contexts/snackNotification';
import { useMutation } from '@sorare/core/src/hooks/graphql/useMutation';
import { useOpenDialog } from '@sorare/core/src/hooks/navigation/useOpenDialog';
import { notificationsQueue } from '@sorare/core/src/hooks/useNotificationsQueue';

import type {
  FootballLoadSuggestLineupMutation,
  FootballLoadSuggestLineupMutationVariables,
} from './__generated__/useLoadSuggestLineup.graphql.ts';

const LOAD_SUGGEST_LINEUP_MUTATION = gql`
  mutation FootballLoadSuggestLineupMutation(
    $input: loadSuggestedLineupInput!
  ) {
    loadSuggestedLineup(input: $input) {
      so5LeaderboardContender {
        slug
        so5Lineup {
          id
        }
        managerTeam {
          id
        }
        so5Leaderboard {
          slug
          so5Fixture {
            slug
            mySo5LineupsCount
            so5LeaderboardGroups {
              slug
              so5LeagueTrackGroups {
                slug
                so5LeagueTracks {
                  slug
                  mySo5LeaderboardContenders {
                    slug
                    so5Lineup {
                      id
                    }
                  }
                }
              }
              eligibleCardsCountByPosition {
                slug
                position
                seasonality
                totalCount
                usedCardsCount
              }
            }
          }
        }
      }
      currentUser {
        slug
        forcedIntoOnboarding
        blockchainCardsInLineups
      }
      errors {
        path
        message
        code
      }
    }
  }
` as TypedDocumentNode<
  FootballLoadSuggestLineupMutation,
  FootballLoadSuggestLineupMutationVariables
>;

export const useLoadSuggestLineup = () => {
  const openDialog = useOpenDialog();
  const [loadSuggestLineup, state] = useMutation(LOAD_SUGGEST_LINEUP_MUTATION, {
    showErrorsInForm: true,
  });

  const wrappedLoadSuggestLineup = useCallback(
    async (...args: Parameters<typeof loadSuggestLineup>) => {
      const result = await loadSuggestLineup(...args);
      const otherErrors = result.errors.filter(
        error => error.code !== JONUM_VERIFICATION_REQUIRED_CODE
      );
      if (otherErrors.length < result.errors.length) {
        openDialog(DialogKey.jonumVerification, 'true');
      }
      if (otherErrors.length > 0) {
        notificationsQueue.addNotification(
          'errors',
          { errors: otherErrors.map(e => e.message).join(', ') },
          { level: Level.ERROR }
        );
      }
      return result;
    },
    [loadSuggestLineup, openDialog]
  );

  return [wrappedLoadSuggestLineup, state] as const;
};
